import { css } from '@emotion/react'
import {
  Alert,
  FormControl,
  HFlow,
  Modal,
  ModalBody,
  ModalFooter,
  ModalFooterButton,
  ModalHeader,
  ModalProps,
  Text,
  VFlow,
} from 'bold-ui'
import { ErrorField, Form, FormRenderProps, TextAreaField } from 'components/form'
import { RadioFieldContainer } from 'components/form/field/RadioFieldContainer'
import { TipoAtendimentoProfissional } from 'graphql/types.generated'
import { metaPath } from 'util/metaPath'

import { AlertaRetificacaoAtendimentoText } from '../AlertaRetificacaoAtendimentoText'
import {
  RetificacaoAtendimentoModel,
  RetificacaoAtendimentoOpcaoEnum,
  tipoAtendimentoRetificacaoRecord,
} from '../model-retificacaoAtendimento'
import { validateRetificacaoAtendimentoModal } from '../validator-retificacaoAtendimento'
import { CabecalhoModalRetificacaoAtendimento } from './CabecalhoModalRetificacaoAtendimento'

export const meta = metaPath<RetificacaoAtendimentoModel>()

interface JustificativaRetificacaoAtendimentoModalProps extends Omit<ModalProps, 'onSubmit'> {
  cidadaoId: ID
  dataAtendimento: Instant
  onCancelar(): void
  onSubmit(values: RetificacaoAtendimentoModel): void
  loading: boolean
  tipoAtendimentoProfissional: TipoAtendimentoProfissional
}

export function JustificativaRetificacaoAtendimentoModal(
  props: Readonly<JustificativaRetificacaoAtendimentoModalProps>
) {
  const { cidadaoId, dataAtendimento, onCancelar, onSubmit, loading, tipoAtendimentoProfissional, ...rest } = props

  const renderForm = ({ handleSubmit, form }: FormRenderProps<RetificacaoAtendimentoModel>) => {
    const handleClose = () => {
      form.reset()
      onCancelar()
    }

    return (
      <form noValidate>
        <Modal size='large' onClose={handleClose} {...rest}>
          <ModalHeader
            title={`Retificar registro do ${tipoAtendimentoRetificacaoRecord[tipoAtendimentoProfissional]?.title}`}
          />
          <ModalBody>
            <VFlow>
              <Alert type='info' styles={styles.alert}>
                <AlertaRetificacaoAtendimentoText
                  tipoAtendimentoProfissional={tipoAtendimentoProfissional}
                  momento='inicial'
                />
              </Alert>
              <CabecalhoModalRetificacaoAtendimento cidadaoId={cidadaoId} dataAtendimento={dataAtendimento} />
              <FormControl label='Tipo de retificação' required>
                <VFlow vSpacing={0.5}>
                  <RadioFieldContainer
                    label={
                      <VFlow vSpacing={0}>
                        <Text fontWeight='bold'>Editar registro</Text>
                        <Text>As edições serão salvas em um novo registro. O registro atual ficará inativo.</Text>
                      </VFlow>
                    }
                    name={meta.tipo}
                    value={RetificacaoAtendimentoOpcaoEnum.EDITAR_REGISTRO}
                    required
                  />
                  <RadioFieldContainer
                    label={
                      <VFlow vSpacing={0}>
                        <Text fontWeight='bold'>Remover registro do prontuário</Text>
                        <Text>Este registro ficará inativo e constará no histórico como removido.</Text>
                      </VFlow>
                    }
                    name={meta.tipo}
                    value={RetificacaoAtendimentoOpcaoEnum.REMOVER_REGISTRO}
                    required
                  />
                  <ErrorField name={meta.tipo} />
                </VFlow>
              </FormControl>
              <TextAreaField
                label='Justificativa'
                name={meta.justificativa}
                maxLength={200}
                required
                style={styles.textAreaField}
              />
            </VFlow>
          </ModalBody>
          <ModalFooter>
            <HFlow justifyContent='flex-end'>
              <ModalFooterButton kind='normal' onClick={handleClose}>
                Cancelar
              </ModalFooterButton>
              <ModalFooterButton kind='primary' onClick={handleSubmit} loading={loading}>
                Retificar
              </ModalFooterButton>
            </HFlow>
          </ModalFooter>
        </Modal>
      </form>
    )
  }

  return (
    <Form<RetificacaoAtendimentoModel>
      render={renderForm}
      onSubmit={onSubmit}
      validate={validateRetificacaoAtendimentoModal}
    />
  )
}

const styles = {
  alert: {
    container: css`
      padding: 0.5rem;
    `,
    wrapper: css`
      padding: 0;
    `,
  },
  textAreaField: css`
    height: 5rem;
    resize: none;
  `,
}
